import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Bold, Italic, Link2, Loader2, Plus, Trash2, Type, List, ListOrdered, Minus, Image as ImageIcon, Pilcrow } from "lucide-react";
import { API, authHeaders, fileUrl } from "@/constants/roles";

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

// Wraps the current textarea selection with inline markup (**bold**, *italic*, [label](url)).
const wrapSelection = (textarea, before, after, onChange) => {
  if (!textarea) return;
  const { selectionStart: s, selectionEnd: e, value } = textarea;
  const selected = value.slice(s, e) || "text";
  const next = value.slice(0, s) + before + selected + after + value.slice(e);
  onChange(next);
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(s + before.length, s + before.length + selected.length);
  });
};

const TextToolbar = ({ areaRef, onChange }) => (
  <div className="mb-1.5 flex gap-1">
    <button type="button" data-testid="fmt-bold" title="Bold" onClick={() => wrapSelection(areaRef.current, "**", "**", onChange)} className="rounded-lg border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-1.5 text-[var(--pv-text-3)] hover:text-cyan-400"><Bold className="h-3.5 w-3.5" /></button>
    <button type="button" data-testid="fmt-italic" title="Italic" onClick={() => wrapSelection(areaRef.current, "*", "*", onChange)} className="rounded-lg border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-1.5 text-[var(--pv-text-3)] hover:text-cyan-400"><Italic className="h-3.5 w-3.5" /></button>
    <button type="button" data-testid="fmt-link" title="Link" onClick={() => wrapSelection(areaRef.current, "[", "](https://)", onChange)} className="rounded-lg border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-1.5 text-[var(--pv-text-3)] hover:text-cyan-400"><Link2 className="h-3.5 w-3.5" /></button>
  </div>
);

// Block-based manual section editor. Blocks: heading / paragraph / bullets / numbers / divider / image.
export const ManualBlockEditor = ({ blocks, onChange }) => {
  const [uploading, setUploading] = useState(-1);
  const fileRefs = useRef({});
  const areaRefs = useRef({});

  const setBlock = (i, patch) => onChange(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = (i) => onChange(blocks.filter((_, j) => j !== i));
  const add = (type) => {
    const blank =
      type === "heading" ? { type, level: 2, text: "" }
      : type === "paragraph" ? { type, text: "" }
      : type === "bullets" || type === "numbers" ? { type, items: [""] }
      : type === "image" ? { type, path: "", caption: "", alt: "" }
      : { type: "divider" };
    onChange([...blocks, blank]);
  };

  const uploadImage = async (i, file) => {
    if (!file) return;
    setUploading(i);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/manuals/images`, form, { headers: authHeaders() });
      setBlock(i, { path: data.path, alt: blocks[i].alt || file.name.replace(/\.[^.]+$/, "") });
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Image upload failed.");
    } finally {
      setUploading(-1);
    }
  };

  const ADD_BUTTONS = [
    ["heading", "Heading", Type],
    ["paragraph", "Text", Pilcrow],
    ["bullets", "Bullet list", List],
    ["numbers", "Numbered list", ListOrdered],
    ["image", "Image", ImageIcon],
    ["divider", "Divider", Minus],
  ];

  return (
    <div data-testid="manual-block-editor" className="space-y-3">
      {blocks.length === 0 && (
        <p data-testid="manual-editor-empty" className="rounded-2xl border border-dashed border-[var(--pv-border-strong)] px-5 py-8 text-center text-xs text-[var(--pv-text-4)]">
          Empty section — add your first block below.
        </p>
      )}
      {blocks.map((b, i) => (
        <div key={i} data-testid={`manual-edit-block-${i}`} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="rounded-full bg-[var(--pv-subtle)] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">{b.type}</span>
            <div className="flex items-center gap-1">
              <button type="button" data-testid={`block-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="rounded-lg p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)] hover:text-cyan-400 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button type="button" data-testid={`block-down-${i}`} onClick={() => move(i, 1)} disabled={i === blocks.length - 1} className="rounded-lg p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)] hover:text-cyan-400 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button type="button" data-testid={`block-delete-${i}`} onClick={() => remove(i)} className="rounded-lg p-1.5 text-[var(--pv-text-4)] hover:bg-rose-500/10 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>

          {b.type === "heading" && (
            <div className="flex gap-2">
              <select data-testid={`block-heading-level-${i}`} value={b.level} onChange={(e) => setBlock(i, { level: Number(e.target.value) })} className={`${inputCls} w-24`}>
                <option value={1}>H1</option>
                <option value={2}>H2</option>
                <option value={3}>H3</option>
              </select>
              <input data-testid={`block-heading-text-${i}`} value={b.text} onChange={(e) => setBlock(i, { text: e.target.value })} placeholder="Heading text" className={inputCls} />
            </div>
          )}

          {b.type === "paragraph" && (
            <div>
              <TextToolbar areaRef={{ get current() { return areaRefs.current[i]; } }} onChange={(text) => setBlock(i, { text })} />
              <textarea ref={(el) => (areaRefs.current[i] = el)} data-testid={`block-paragraph-text-${i}`} rows={4} value={b.text} onChange={(e) => setBlock(i, { text: e.target.value })} placeholder="Write instructions… Use **bold**, *italic* and [links](https://…)" className={inputCls} />
            </div>
          )}

          {(b.type === "bullets" || b.type === "numbers") && (
            <textarea data-testid={`block-list-items-${i}`} rows={Math.min(8, (b.items?.length || 1) + 1)} value={(b.items || []).join("\n")} onChange={(e) => setBlock(i, { items: e.target.value.split("\n") })} placeholder="One item per line" className={inputCls} />
          )}

          {b.type === "image" && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" data-testid={`block-image-upload-${i}`} onClick={() => fileRefs.current[i]?.click()} disabled={uploading === i} className="flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-xs font-bold text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50">
                  {uploading === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                  {b.path ? "Replace image" : "Upload image / screenshot"}
                </button>
                <input ref={(el) => (fileRefs.current[i] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(i, e.target.files?.[0])} />
                {b.path && <span className="text-[10px] font-semibold text-[var(--pv-text-4)]">{b.path.split("/").pop()}</span>}
              </div>
              {b.path && <img src={fileUrl(b.path)} alt={b.alt || ""} className="max-h-48 rounded-xl border border-[var(--pv-border)] object-contain" />}
              <input data-testid={`block-image-caption-${i}`} value={b.caption || ""} onChange={(e) => setBlock(i, { caption: e.target.value })} placeholder="Caption (shown under the image)" className={inputCls} />
            </div>
          )}

          {b.type === "divider" && <hr className="border-[var(--pv-border)]" />}
        </div>
      ))}

      <div className="flex flex-wrap gap-1.5 rounded-2xl border border-dashed border-[var(--pv-border-strong)] p-3">
        {ADD_BUTTONS.map(([type, label, Icon]) => (
          <button key={type} type="button" data-testid={`block-add-${type}`} onClick={() => add(type)} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/30 hover:text-cyan-400">
            <Plus className="h-3 w-3" /> <Icon className="h-3 w-3" /> {label}
          </button>
        ))}
      </div>
    </div>
  );
};
